import type { WidgetConfig } from './types.js';

/**
 * The price box under the form, the way WAPF showed it: the product's own
 * price, each paid extra on its own line, and the total — with the regular
 * price struck through beside a sale price, and what the shopper saves.
 *
 * The product's price is WooCommerce's, read from the page it already printed:
 * a variable product's variation data (`data-product_variations`, the same
 * numbers Woo shows when a variation is picked), otherwise the price element
 * in the product summary. Nothing here decides a price — the cart line is Woo's
 * price plus the surcharge the server billed — so a theme whose price cannot
 * be read simply gets the extras and their sum, as before.
 */

export interface PagePrice {
  /** What the shopper pays per item, before extras. */
  price: number;
  /** The price before a sale; equal to `price` when there is none. */
  regular: number;
}

export interface PriceLine {
  label: string;
  amount: number;
}

type Currency = WidgetConfig['currency'];

/**
 * "2.990,00RSD", "1,299.50 $", "2.290" → a number, with the shop's own
 * separators. Anything that is not one amount (a range "1.990 – 2.990") is null.
 */
export function parseAmount(text: string, currency: Pick<Currency, 'decimalSep' | 'thousandSep' | 'decimals'>): number | null {
  const kept = text.replace(/[^\d.,\s '’-]/g, '').trim();
  if (!kept || /\d\s*[-–]\s*\d/.test(text)) return null;
  let digits = kept.replace(/[\s ]/g, '');
  if (currency.thousandSep) digits = digits.split(currency.thousandSep).join('');
  if (currency.decimalSep && currency.decimalSep !== '.') digits = digits.replace(currency.decimalSep, '.');
  digits = digits.replace(/[^\d.]/g, '');
  if (!/^\d+(\.\d+)?$/.test(digits)) return null;
  const value = Number(digits);
  return Number.isFinite(value) ? value : null;
}

/** Reads one Woo price element: `<del>` regular + `<ins>` sale, or a single amount. */
export function readPriceElement(element: Element, currency: Currency): PagePrice | null {
  // Woo's amount spans, never the whole text: themes add screen-reader words
  // ("Originalna cena je bila: …") that repeat every figure.
  const amount = (root: Element | null): number | null => {
    const node = root?.querySelector('.woocommerce-Price-amount') ?? null;
    return node ? parseAmount(node.textContent ?? '', currency) : null;
  };
  const del = element.querySelector('del');
  const ins = element.querySelector('ins');
  if (del && ins) {
    const regular = amount(del);
    const price = amount(ins);
    if (regular !== null && price !== null) return { price, regular: Math.max(regular, price) };
  }
  const all = element.querySelectorAll('.woocommerce-Price-amount');
  // Two plain amounts are a range ("od 1.990 do 2.990"): no single price yet.
  if (all.length !== 1) return null;
  const single = parseAmount(all[0]!.textContent ?? '', currency);
  return single === null ? null : { price: single, regular: single };
}

/** The price of one variation from Woo's own variation data, when the page carries it. */
function variationPrice(form: HTMLFormElement | null, variationId: number | null): PagePrice | null {
  if (!form || !variationId) return null;
  const raw = form.getAttribute('data-product_variations');
  if (!raw || raw === 'false') return null;
  try {
    const list = JSON.parse(raw) as Array<{ variation_id?: number; display_price?: number; display_regular_price?: number }>;
    const found = list.find((v) => Number(v.variation_id) === variationId);
    const price = Number(found?.display_price);
    if (!found || !Number.isFinite(price)) return null;
    const regular = Number(found.display_regular_price);
    return { price, regular: Number.isFinite(regular) && regular > price ? regular : price };
  } catch {
    return null;
  }
}

/**
 * The product's price as the page shows it for the current variation, or null
 * when it cannot be told (a variable product before a variation is picked, a
 * theme without Woo's price markup).
 */
export function readPagePrice(currency: Currency, variationId: number | null, doc: Document = document): PagePrice | null {
  const form = doc.querySelector<HTMLFormElement>('form.variations_form');
  if (form) {
    const fromData = variationPrice(form, variationId);
    if (fromData) return fromData;
    // Many variations: Woo fetches them one by one and prints the picked
    // one's price into the form instead of carrying the list.
    const shown = form.querySelector('.woocommerce-variation-price .price');
    if (variationId && shown) return readPriceElement(shown, currency);
    if (variationId) return null;
  }
  const summary =
    doc.querySelector('.summary .price') ??
    doc.querySelector('.wp-block-woocommerce-product-price .price, .wc-block-components-product-price') ??
    doc.querySelector('.product .price');
  return summary ? readPriceElement(summary, currency) : null;
}

/** How many the shopper is buying, from Woo's quantity field; 1 when there is none. */
export function readQuantity(doc: Document = document): number {
  const field = doc.querySelector<HTMLInputElement>('form.cart input[name="quantity"], form.cart input.qty');
  const value = Math.floor(Number(field?.value));
  return Number.isFinite(value) && value >= 1 ? Math.min(value, 9999) : 1;
}

export const PRICE_TEXTS = {
  base: 'Cena proizvoda',
  extras: 'Dodaci',
  quantity: 'Količina',
  total: 'Ukupno',
  extrasOnly: 'Personalizacija',
  saved: (amount: string, percent: number) => `Ušteda ${amount}${percent >= 1 ? ` (−${percent}%)` : ''}`,
  deal: (percent: number, pieces: number) => `Popust za ${pieces} kom. (−${percentText(percent)})`,
  sale: (percent: number) => `Akcija${percent >= 1 ? ` (−${percent}%)` : ''}`,
};

/** "5", "7,5" */
export function percentText(percent: number): string {
  return String(Math.round(percent * 100) / 100).replace('.', ',') + '%';
}

/** A pieces deal's step: "2 kom −5%". */
export interface DealTier {
  min: number;
  percent: number;
}

/** The step `pieces` reach (the highest whose minimum they meet), or undefined. */
export function dealStep(tiers: readonly DealTier[] | undefined, pieces: number): DealTier | undefined {
  let hit: DealTier | undefined;
  for (const tier of tiers ?? []) if (pieces >= tier.min && (!hit || tier.min > hit.min)) hit = tier;
  return hit;
}

/** The next step above `pieces`, or undefined at the top. */
export function nextDealStep(tiers: readonly DealTier[] | undefined, pieces: number): DealTier | undefined {
  let next: DealTier | undefined;
  for (const tier of tiers ?? []) if (pieces < tier.min && (!next || tier.min < next.min)) next = tier;
  return next;
}

export interface PriceSummaryInput {
  page: PagePrice | null;
  lines: PriceLine[];
  quantity: number;
  format: (amount: number) => string;
  /** A pieces deal reached by the quantity: its percent off the whole line. */
  dealPercent?: number;
}

/** The totals the box shows; kept apart from the DOM so they can be tested. */
export function priceTotals(input: Pick<PriceSummaryInput, 'page' | 'lines' | 'quantity' | 'dealPercent'>): {
  extras: number;
  total: number | null;
  regularTotal: number | null;
  saved: number;
  percent: number;
  /** What the pieces deal takes off (included in `saved`). */
  deal: number;
} {
  const extras = Math.round(input.lines.reduce((sum, line) => sum + line.amount, 0) * 100) / 100;
  if (!input.page) return { extras, total: null, regularTotal: null, saved: 0, percent: 0, deal: 0 };
  const qty = Math.max(1, input.quantity);
  const full = Math.round((input.page.price + extras) * qty * 100) / 100;
  // The cart rounds each piece's discounted price; so does this.
  const off = input.dealPercent && input.dealPercent > 0 ? input.dealPercent : 0;
  const piece = Math.round((input.page.price + extras) * (1 - off / 100) * 100) / 100;
  const total = off > 0 ? Math.round(piece * qty * 100) / 100 : full;
  const deal = Math.max(0, Math.round((full - total) * 100) / 100);
  const regularTotal = Math.round((input.page.regular + extras) * qty * 100) / 100;
  const saved = Math.max(0, Math.round((regularTotal - total) * 100) / 100);
  const percent = regularTotal > 0 ? Math.round((saved / regularTotal) * 100) : 0;
  return { extras, total, regularTotal, saved, percent, deal };
}

/** Draws the box; empty (hidden) when there is neither a price nor an extra. */
/** What each box last showed: the same sums are not drawn again (uploads refresh it many times a second). */
const drawnSummary = new WeakMap<HTMLElement, string>();

export function renderPriceSummary(box: HTMLElement, input: PriceSummaryInput): void {
  const { page, lines, quantity, format } = input;
  const totals = priceTotals(input);
  const signature = JSON.stringify([page, lines, quantity, totals, format(1234.5)]);
  if (drawnSummary.get(box) === signature) return;
  drawnSummary.set(box, signature);
  box.innerHTML = '';
  box.hidden = !page && totals.extras <= 0;
  // Something beyond the total (a saving, a paid extra): a phone's popup, whose
  // footer already shows the total, keeps only these lines.
  box.classList.toggle('has-more', totals.saved > 0 || lines.length > 0);
  if (box.hidden) return;

  const row = (label: string, value: Node | string, className = ''): HTMLElement => {
    const el = document.createElement('div');
    el.className = `pz-pricebox-row ${className}`.trim();
    const name = document.createElement('span');
    name.className = 'pz-pricebox-label';
    name.textContent = label;
    const amount = document.createElement('span');
    amount.className = 'pz-pricebox-value';
    amount.append(value);
    el.append(name, amount);
    return el;
  };
  const money = (now: number, before?: number): DocumentFragment => {
    const fragment = document.createDocumentFragment();
    if (before !== undefined && before > now + 0.004) {
      const del = document.createElement('del');
      del.textContent = format(before);
      fragment.append(del, ' ');
    }
    const strong = document.createElement('b');
    strong.textContent = format(now);
    fragment.append(strong);
    return fragment;
  };

  // With nothing added and one piece, the product's price IS the total: one row, not two.
  const alone = lines.length === 0 && quantity <= 1;
  // A ledger that adds up: the regular price, then each saving on its own
  // line — the shop's sale and the pieces deal — so both are seen, not only
  // a struck-through figure.
  const qty = Math.max(1, quantity);
  const sale = page && page.regular > page.price + 0.004 ? Math.round((page.regular - page.price) * qty * 100) / 100 : 0;
  if (page && !alone) box.appendChild(row(PRICE_TEXTS.base, money(page.regular), 'is-base'));
  if (lines.length > 0) {
    if (page) {
      const head = document.createElement('div');
      head.className = 'pz-pricebox-head';
      head.textContent = PRICE_TEXTS.extras;
      box.appendChild(head);
    }
    for (const line of lines) {
      box.appendChild(row(line.label, `${line.amount < 0 ? '−' : '+'}${format(Math.abs(line.amount))}`, 'is-extra'));
    }
  }
  if (page && quantity > 1) box.appendChild(row(PRICE_TEXTS.quantity, `× ${quantity}`, 'is-qty'));
  if (sale > 0 && !alone) {
    box.appendChild(row(PRICE_TEXTS.sale(Math.round(((page!.regular - page!.price) / page!.regular) * 100)), `−${format(sale)}`, 'is-deal'));
  }
  if (totals.deal > 0 && input.dealPercent) {
    box.appendChild(row(PRICE_TEXTS.deal(input.dealPercent, quantity), `−${format(totals.deal)}`, 'is-deal'));
  }

  if (!alone || !page) {
    const rule = document.createElement('div');
    rule.className = 'pz-pricebox-rule';
    box.appendChild(rule);
  }

  if (totals.total !== null) {
    box.appendChild(row(PRICE_TEXTS.total, money(totals.total, totals.regularTotal ?? undefined), 'is-total'));
    if (totals.saved > 0) {
      const saved = document.createElement('div');
      saved.className = 'pz-pricebox-saved';
      saved.textContent = PRICE_TEXTS.saved(format(totals.saved), totals.percent);
      box.appendChild(saved);
    }
  } else {
    box.appendChild(row(PRICE_TEXTS.extrasOnly, `+${format(totals.extras)}`, 'is-total'));
  }
}
